// eslint-disable-next-line no-use-before-define
import { PatientCard } from 'op-components';
import SidePatientNav from 'op-components/SideNav/SidePatientNav';
import { Fragment, useContext, useEffect, useRef, useState } from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import { useRouteMatch } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { useOnClickOutside } from 'shared-components/utils/CustomHooks';
import { styled } from '@mui/system';
import {
  AlertsCard,
  AllergiesCard,
  AppointmentsCard,
  DiagnosisCard,
  EmergencyContactCard,
  InsuranceCard,
  TreatmentTeamCard,
} from './PatientDetailsCards';
import './PatientSummary.scss';
import { ROPatientContext } from './context';
import { Stack, IconButton, Box, Collapse, Fade, CircularProgress } from '@mui/material';
import { CurrentAppConfig } from 'op-pages/RO/Careplan/AppConfig';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { NavigationContext } from 'op-contexts/index';
import dayjs from 'dayjs';
import { useLazyQuery } from '@apollo/client';
import { GET_RO_PATIENT_MORE_DETAILS } from 'op-components/PatientCard/PatientCardQueries';

const MiddleContainer = styled(Stack)`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  height: 100%;
  max-height: 100vh;
`;

const StyedPatientSummaryRow = styled(Stack, {
  shouldForwardProp: (prop) => !['$rowSpace', '$marginBottom'].includes(prop as string),
})<{ $marginBottom?: number | string; $rowSpace?: number | string }>`
  position: relative;
  min-height: 0;
  flex: 1;
  height: 100%;
  padding: ${(props) => props.$rowSpace || '16px 0'};
`;

const StyledROSidePatientNavCol = styled(Stack, {
  shouldForwardProp: (prop) => prop !== '$collapsed',
})<{ $collapsed: boolean }>`
  max-width: ${(props) => (props.$collapsed ? '60px' : '240px')};
  min-width: ${(props) => (props.$collapsed ? '60px' : '240px')};
  background-color: ${(props) => props.theme.palette.background.secondary};
  border-right: 1px solid rgba(0, 0, 0, 0.2);
  position: relative;
  transition: all 0.3s ease;
`;

const StyledROSidePatientNav = styled(SidePatientNav)<{ $collapsed: boolean }>`
  max-width: ${(props) => (props.$collapsed ? '60px' : '240px')};
  min-width: ${(props) => (props.$collapsed ? '60px' : '240px')};
  height: 100vh;
  transition: all 0.3s ease;
`;

const CollapseButton = styled(IconButton)`
  position: absolute;
  top: 70px;
  right: -15px;
  z-index: 10;
  background-color: white;
  border: 1px solid rgba(0, 0, 0, 0.2);
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);
  width: 30px;
  height: 30px;

  &:hover {
    background-color: #f5f5f5;
  }
`;

const MoreDetailsContainer = styled('div')`
  position: absolute;
  top: 0px;
  width: 100%;
  z-index: 49;
  padding: 16px;
  overflow: visible;
  background: ${(props) => props.theme.palette.background.default};
  height: fit-content;
`;

const emptyGridTemplateAreas =
  "'physician appointments diagnosis alerts' 'alternate-contact insurance diagnosis allergies'";
const smallGridTemplateAreas =
  "'physician appointments diagnosis alerts' 'alternate-contact insurance diagnosis allergies'";
const largeGridTemplateAreas =
  "'physician appointments diagnosis alerts' 'alternate-contact insurance diagnosis allergies'";
const MoreDetailsBody = styled('div')<{ gridAreas: string }>`
  background-color: white;
  width: 100%;
  margin: 0;
  display: grid;
  gap: 16px;
  grid-template-areas: ${(props) => props.gridAreas};
  grid-template-columns: 1fr 1fr 2fr 1fr;
  grid-template-rows: repeat(2, 350px);
  overflow: auto;
`;

const MoreInfoBox = styled('div')<{ gridColumnSpan?: number; gridRowSpan?: number }>`
  border: 1px solid ${(props) => props.theme.palette.grey[300]};
  border-radius: 4px;
  width: 100%;
  height: 100%;
  margin: 0;
  display: flex;
  flex-direction: column;
`;

interface Props extends RouteComponentProps {
  children: JSX.Element | JSX.Element[];
  refreshChartSummary?: any;
  patientCardView?: string;
  rowSpace?: string;
  marginBottom?: string;
}

const OuterContainer = ({
  children,
  refreshChartSummary,
  patientCardView,
  rowSpace,
  marginBottom,
}: Props): JSX.Element => {
  const [showMoreDetails, setShowMoreDetails] = useState(false);
  const match = useRouteMatch<any>();
  const { id: patientId } = match.params;

  const targetRef = useRef<HTMLDivElement>(null);
  const { patientDetails, patientAlerts, moreDetails, setMoreDetails, moreDetailsLoading, setMoreDetailsLoading } =
    useContext(ROPatientContext);
  const { isSideNavbarCollapsed, setIsSideNavbarCollapsed } = useContext(NavigationContext);
  const [showText, setShowText] = useState(!isSideNavbarCollapsed);
  const cardRef = useRef<HTMLDivElement>(null);
  const [cardWidth, setCardWidth] = useState<number>(0);

  // Lazy query for more details panel - only fetched when panel is opened
  const [fetchMoreDetails, { loading: moreDetailsQueryLoading }] = useLazyQuery(GET_RO_PATIENT_MORE_DETAILS, {
    variables: { patientId },
    fetchPolicy: 'cache-and-network',
    onCompleted: (data) => {
      if (data?.patient) {
        setMoreDetails(data.patient);
        setMoreDetailsLoading(false);
      }
    },
  });

  useEffect(() => {
    const handleResize = () => {
      if (cardRef.current) {
        const { width } = cardRef.current.getBoundingClientRect();
        if (width >= 1500 && width / 6 < 262) {
          return setCardWidth(20);
        }
        if (width < 1500 && width / 6 < 262) {
          return setCardWidth(25);
        } else return setCardWidth(16.666667);
      }
    };
    // Add event listener
    window.addEventListener('resize', handleResize);
    // Call handler right away so state gets updated with initial window size
    handleResize();
    // Remove event listener on cleanup
    return () => window.removeEventListener('resize', handleResize);
  });

  // hide more details and clear cached data when patientId changes
  useEffect(() => {
    if (patientId) {
      setShowMoreDetails(false);
      setMoreDetails(undefined);
    }
  }, [patientId]);

  // Sync loading state
  useEffect(() => {
    setMoreDetailsLoading(moreDetailsQueryLoading);
  }, [moreDetailsQueryLoading]);

  const moreDetailsClickHandler = () => {
    if (!showMoreDetails) {
      // Fetch more details when opening the panel (if not already cached)
      if (!moreDetails) {
        fetchMoreDetails();
      }
    }
    setShowMoreDetails(!showMoreDetails);
  };

  useOnClickOutside(targetRef, (event: any) => {
    if (typeof event?.target?.className === 'string' && event?.target?.className.includes('more-details-overlay')) {
      setShowMoreDetails(false);
    }
  });

  const toggleCollapse = () => {
    if (!isSideNavbarCollapsed) {
      setShowText(false);
      setTimeout(() => {
        setIsSideNavbarCollapsed(true);
      }, 200);
    } else {
      setIsSideNavbarCollapsed(false);
      setTimeout(() => {
        setShowText(true);
      }, 200);
    }
  };

  // When the isSideNavbarCollapsed state changes from context, update the showText state
  useEffect(() => {
    if (isSideNavbarCollapsed) {
      setShowText(false);
    } else {
      setTimeout(() => {
        setShowText(true);
      }, 200);
    }
  }, [isSideNavbarCollapsed]);

  const hasDiagnosis =
    patientDetails?.carepathPatientDiagnosis?.filter(
      (diagnosis: any) => diagnosis?.isMostRecent && diagnosis?.emrDiagnosisKey,
    )?.length > 0;

  const gridAreas = hasDiagnosis
    ? cardRef.current?.getBoundingClientRect()?.width && cardRef.current?.getBoundingClientRect()?.width > 1500
      ? largeGridTemplateAreas
      : smallGridTemplateAreas
    : emptyGridTemplateAreas;

  return (
    <Fragment>
      <Stack
        id="patient-summary-primary"
        sx={{
          display: 'flex',
          flexDirection: 'row',
          height: '100%',
          width: '100%',
          position: 'absolute',
          paddingTop: '56px',
        }}>
        <StyledROSidePatientNavCol $collapsed={isSideNavbarCollapsed}>
          <CollapseButton onClick={toggleCollapse} size="small">
            {isSideNavbarCollapsed ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
          </CollapseButton>
          <StyledROSidePatientNav
            $collapsed={!showText}
            patientId={patientId}
            refreshChartSummary={refreshChartSummary}
          />
        </StyledROSidePatientNavCol>

        <MiddleContainer ref={cardRef}>
          <PatientCard
            id={patientId}
            simple={false}
            moreDetailsClickHandler={moreDetailsClickHandler}
            showMoreDetails={showMoreDetails}
            patientCardView={patientCardView}
          />
          <StyedPatientSummaryRow
            data-testid="patient-summary-row"
            $rowSpace={rowSpace}
            $marginBottom={marginBottom}
            style={{
              display: 'flex',
              flexDirection: 'row',
              flex: 1,
              minHeight: 0,
              borderTop: '1px solid rgba(8, 30, 67, 0.12)',
              boxShadow: '0px 1px 1px rgba(0, 0, 0, 0.2)',
            }}>
            <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', zIndex: 48 }}>
              <Collapse in={showMoreDetails} timeout={400}>
                <Box>
                  <Fade in={showMoreDetails} timeout={400}>
                    <Box
                      className="more-details-overlay"
                      sx={{
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        height: '100vh',
                        width: '100%',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        zIndex: 48,
                        pointerEvents: showMoreDetails ? 'auto' : 'none',
                      }}></Box>
                  </Fade>
                  <MoreDetailsContainer className="more-details-container" sx={{ position: 'relative' }}>
                    <MoreDetailsBody className="more-details-body" ref={targetRef} gridAreas={gridAreas}>
                      {moreDetailsLoading || !moreDetails ? (
                        <Box
                          sx={{
                            gridColumn: '1 / -1',
                            gridRow: '1 / -1',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                          }}>
                          <CircularProgress />
                        </Box>
                      ) : (
                        patientDetails && (
                          <>
                            <MoreInfoBox style={{ gridArea: 'physician' }}>
                              <TreatmentTeamCard
                                name={'Treating Physicians'}
                                allReferrers={patientDetails?.allReferrers}
                              />
                            </MoreInfoBox>
                            <MoreInfoBox style={{ gridArea: 'appointments' }}>
                              <AppointmentsCard
                                name={'Upcoming Appointments'}
                                appointments={moreDetails.appointments || []}
                              />
                            </MoreInfoBox>
                            <MoreInfoBox style={{ gridArea: 'alternate-contact' }}>
                              <EmergencyContactCard name={'Emergency Contact'} patientDetails={moreDetails} />
                            </MoreInfoBox>
                            {CurrentAppConfig?.PatientSummary?.hideInsuranceTab ? null : (
                              <MoreInfoBox style={{ gridArea: 'insurance' }}>
                                <InsuranceCard name={'Insurance'} patientDetails={moreDetails} />
                              </MoreInfoBox>
                            )}
                            <MoreInfoBox style={{ gridArea: 'allergies' }}>
                              <AllergiesCard name={'Allergies'} allergies={patientDetails?.allergy} />
                            </MoreInfoBox>
                            <MoreInfoBox style={{ gridArea: 'alerts' }}>
                              <AlertsCard name={'Alerts'} alerts={patientAlerts} />
                            </MoreInfoBox>
                            <MoreInfoBox style={{ gridArea: 'diagnosis', minHeight: '0', minWidth: '460px' }}>
                              <DiagnosisCard
                                name={'Diagnosis'}
                                patientDiagnosis={(moreDetails.carepathPatientDiagnosis || [])
                                  .filter((diagnosis: any) => diagnosis?.isMostRecent && diagnosis?.emrDiagnosisKey)
                                  .sort((a: any, b: any) => dayjs(b.diagnosisDate).diff(dayjs(a.diagnosisDate)))}
                              />
                            </MoreInfoBox>
                          </>
                        )
                      )}
                    </MoreDetailsBody>
                  </MoreDetailsContainer>
                </Box>
              </Collapse>
            </Box>
            {children}
          </StyedPatientSummaryRow>
        </MiddleContainer>
      </Stack>
      <ToastContainer limit={3} style={{ width: 'fit-content' }} />
    </Fragment>
  );
};

const routedComponent = withRouter(OuterContainer);
export default routedComponent;
